import { getAuthToken } from '@dynamic-labs/sdk-react-core';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { ASTNode, print } from 'graphql';

import { GET_INDEXED_EVM_NFT_BY_TEMPLATE_ID_QUERY } from '@repo/api-core-toolkit';
import { ChainHookQueryOptions } from '@repo/types-client';

/**
 * Custom hook to fetch indexed EVM NFT metadata by template ID using a GraphQL query.
 * @function useGetIndexedEvmNftByTemplateIdQuery
 * @template EVMNftWithStats - The type of the response data.
 * @param {ChainHookQueryOptions} options - The options for the GraphQL query.
 * @param {unknown[]} options.queryKey - The query key for React Query.
 * @param {Record<string, any>} options.data - The variables for the GraphQL query.
 * @param {boolean} [options.enabled=true] - Whether the query is enabled.
 * @returns {UseQueryResult<EVMNftWithStats>} The result of the query.
 */
export function useGetIndexedEvmNftByTemplateIdQuery<EVMNftWithStats>({
  queryKey,
  data,
  enabled = true,
}: ChainHookQueryOptions): UseQueryResult<EVMNftWithStats> {
  const dynamicAccessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
    Authorization: dynamicAccessToken ? `Bearer ${dynamicAccessToken}` : '',
  };

  return useQuery<EVMNftWithStats, Error>({
    queryKey,
    queryFn: async () => {
      const response = await fetch(process.env.NEXT_PUBLIC_GQL_API_URL ?? '', {
        headers,
        method: 'POST',
        body: JSON.stringify({
          query: print(GET_INDEXED_EVM_NFT_BY_TEMPLATE_ID_QUERY as ASTNode),
          variables: { data },
        }),
      });
      const result = await response.json();
      return result.data.getIndexedEvmNFTByTemplateId;
    },
    refetchOnWindowFocus: false,
    enabled,
  });
}
